<template>
  <!--
    AT-API-020-H3 (doc 34/03 §7) -- the sources a tutor reply rests on, under
    the reply. 36_TUTOR_POST_UAT_CORRECTIONS TC-3 (doc 36/01 §4, D-3): the
    canonical V405 presentation -- one collapsed control (book icon, label,
    chevron; `.message-source-toggle`) that opens the source detail card
    (`.message-source-detail`: «کتاب» / «صفحه» cells). Rows are grouped for
    display only (`groupSources`): the same source on the same printed page
    is ONE card whose «ارجاع» cell lists the marker numbers it stands for (the
    provider's numbering, untouched; it takes the place of V405's «نسخه»,
    which the provider does not give). A citation without a reliable source
    identity is its own card, never merged by title. Nothing here loads
    anything: no identifier, image or URL of a source is rendered or placed
    in the DOM. Every value is bound as text; the block follows the bubble's
    direction.

    Package 38 UX-2 (doc 38/00 §5): a card whose source has a page image
    opens that page in the SAME viewer the in-text markers open. The card
    stays exactly as it was; a transparent button stretched over it
    (`.ai-tutor-sources__open`) makes the whole card one control, labelled
    like the marker it stands for. Activating it emits `open-citation` with
    the marker NUMBER only (`group.openN`) -- the list resolves the page and
    its signed URL exactly as for a marker. A card without a page image
    stays inert.
  -->
  <section
    v-if="groups.length"
    class="ai-tutor-sources"
    :class="{ 'ai-tutor-sources--open': open }"
    data-testid="ai-tutor-sources"
    :aria-label="$t('aiTutorSetup.chat.sources.label')"
  >
    <button
      type="button"
      class="ai-tutor-sources__toggle"
      data-testid="ai-tutor-sources-toggle"
      :aria-expanded="open ? 'true' : 'false'"
      :aria-controls="detailId"
      @click="open = !open"
    >
      <span class="ai-tutor-sources__title">
        <student-home-icon name="book" :size="15" />
        {{ toggleLabel }}
      </span>
      <student-home-icon
        class="ai-tutor-sources__chevron"
        name="arrow"
        :size="14"
      />
    </button>
    <ol
      v-show="open"
      :id="detailId"
      class="ai-tutor-sources__list"
      data-testid="ai-tutor-sources-list"
    >
      <li
        v-for="group in groups"
        :key="group.key"
        class="ai-tutor-sources__item"
        :class="{ 'ai-tutor-sources__item--open': group.openN !== null }"
        :data-testid="`ai-tutor-source-${group.numbers[0]}`"
      >
        <div class="ai-tutor-sources__cell ai-tutor-sources__cell--book">
          <span>{{ $t('aiTutorSetup.chat.sources.cellBook') }}</span>
          <strong class="ai-tutor-sources__text">{{
            cellsFor(group).book
          }}</strong>
        </div>
        <div
          v-if="cellsFor(group).page"
          class="ai-tutor-sources__cell ai-tutor-sources__cell--page"
        >
          <span>{{ $t('aiTutorSetup.chat.sources.cellPage') }}</span>
          <strong data-testid="ai-tutor-source-page">{{
            cellsFor(group).page
          }}</strong>
        </div>
        <div class="ai-tutor-sources__cell ai-tutor-sources__cell--refs">
          <span>{{ $t('aiTutorSetup.chat.sources.cellRefs') }}</span>
          <strong
            class="ai-tutor-sources__n"
            data-testid="ai-tutor-source-numbers"
            >{{ numbersText(group) }}</strong
          >
        </div>
        <button
          v-if="group.openN !== null"
          type="button"
          class="ai-tutor-sources__open"
          :data-testid="`ai-tutor-source-open-${group.openN}`"
          :aria-label="$t('aiTutorSetup.chat.sources.open', { n: group.openN })"
          @click="openPage(group, $event)"
        ></button>
      </li>
    </ol>
  </section>
</template>

<script>
import StudentHomeIcon from '@/components/StudentHome/StudentHomeIcon.vue'
import { groupSources, sourceGroupCells } from './sourceLine'

let uid = 0

export default {
  name: 'ChatSources',
  components: { StudentHomeIcon },
  props: {
    /** @type {import('vue').PropOptions<import('@/services/aiTutor/domain').TutorSource[]>} */
    sources: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    uid += 1
    return {
      // V405: collapsed by default; the state is this reply's own.
      open: false,
      detailId: `ai-tutor-sources-${uid}`,
    }
  },
  computed: {
    groups() {
      return groupSources(this.sources)
    },
    toggleLabel() {
      return this.groups.length > 1
        ? this.$t('aiTutorSetup.chat.sources.toggleMany', {
            count: this.groups.length,
          })
        : this.$t('aiTutorSetup.chat.sources.toggleOne')
    },
  },
  methods: {
    cellsFor(group) {
      return sourceGroupCells(group, (key, values) => this.$t(key, values))
    },
    numbersText(group) {
      const separator = this.$i18n && this.$i18n.locale === 'fa' ? '، ' : ', '
      return group.numbers.join(separator)
    },
    openPage(group, event) {
      this.$emit('open-citation', {
        n: group.openN,
        opener: event.currentTarget,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
// V405 `.message-source` (BORNA_final405-07-06.html, doc 36/01 §4.1), the
// values as RENDERED there (doc 36/03 §6: the prototype's tutor bubble and
// this app's are the same 12px / 22.8px type, so its sizes apply as they
// are): a 1px separator above, the toggle row in V405 `--indigo-700`
// (#243ab4 = Borna's $borna-indigo-900) with a 9px / 800 label, and the
// detail card in indigo-50 / indigo-100, 11px radius, label-over-value cells
// (7px muted / 8px 700 midnight) on a `1fr auto auto` grid.
.ai-tutor-sources {
  margin-top: 13px;
  padding-top: 10px;
  border-top: 1px solid $borna-border;
}

.ai-tutor-sources__toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  padding: 5px 0;
  border: 0;
  background: none;
  color: $borna-indigo-900;
  font: inherit;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid $borna-indigo-300;
    outline-offset: 2px;
    border-radius: 6px;
  }
}

.ai-tutor-sources__title {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 9px;
  font-weight: 800;
  line-height: 1.9;
}

.ai-tutor-sources__list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.ai-tutor-sources__item {
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 8px;
  margin-top: 8px;
  padding: 10px;
  border: 1px solid $borna-indigo-100;
  border-radius: 11px;
  background: $borna-indigo-50;
}

// Package 38 UX-2: the card is the control. Its box is unchanged; the
// stretched button covers it exactly and draws the focus ring the toggle uses.
.ai-tutor-sources__item--open {
  position: relative;
  cursor: pointer;
}

.ai-tutor-sources__open {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: inherit;
  background: transparent;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid $borna-indigo-300;
    outline-offset: 2px;
  }
}

.ai-tutor-sources__cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;

  span {
    color: $borna-muted;
    font-size: 7px;
    line-height: 1.9;
  }

  strong {
    color: $borna-ink;
    font-size: 8px;
    font-weight: 700;
    line-height: 1.7;
    overflow-wrap: anywhere;
  }
}

.ai-tutor-sources__n {
  font-variant-numeric: tabular-nums;
}

// V405 at (width <= 620px): two columns, the book on its own row.
@media (max-width: 620px) {
  .ai-tutor-sources__item {
    grid-template-columns: 1fr 1fr;
  }

  .ai-tutor-sources__cell--book {
    grid-column: 1 / -1;
  }
}
</style>
